/**
 * RegistrationDetailPanel — right slide-out panel for registration details
 * Shows: player info, parent/guardian, emergency contacts, payment details,
 * signed documents, medical info, addons, and registration metadata.
 */
import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  User, Mail, Phone, Calendar, MapPin, Heart, Shield,
  CreditCard, RefreshCw, FileText, Download, Eye,
  Clock, Users, Loader2, ChevronDown, ChevronUp,
  AlertTriangle, Package, DollarSign, Receipt, ExternalLink,
} from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────
const calculateAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return format(new Date(d), "MMM d, yyyy"); } catch { return d; }
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  try { return format(new Date(d), "MMM d, yyyy 'at' h:mm a"); } catch { return d; }
};

const fmtMoney = (v) => {
  if (v == null) return "$0.00";
  return `$${Number(v).toFixed(2)}`;
};

// ── Section wrapper (collapsible) ────────────────────────
const Section = ({ title, icon: Icon, children, color = "slate", defaultOpen = true, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  const bgMap = {
    slate: "bg-slate-50 border-slate-200",
    rose: "bg-rose-50 border-rose-200",
    amber: "bg-amber-50 border-amber-200",
    emerald: "bg-emerald-50 border-emerald-200",
    blue: "bg-blue-50 border-blue-200",
    purple: "bg-purple-50 border-purple-200",
  };
  const iconColorMap = {
    slate: "text-slate-500",
    rose: "text-rose-500",
    amber: "text-amber-500",
    emerald: "text-emerald-500",
    blue: "text-blue-500",
    purple: "text-purple-500",
  };
  return (
    <div className={`rounded-xl border ${bgMap[color] || bgMap.slate}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${iconColorMap[color]}`} />}
          <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
          {badge}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
};

// ── Field row ────────────────────────────────────────────
const Field = ({ label, value, href, className = "" }) => (
  <div className={className}>
    <p className="text-xs text-slate-500">{label}</p>
    {href ? (
      <a href={href} className="text-sm font-medium text-blue-600 hover:underline">{value || "—"}</a>
    ) : (
      <p className="text-sm font-medium text-slate-900">{value || "—"}</p>
    )}
  </div>
);

// ── Status badge ─────────────────────────────────────────
const StatusBadge = ({ status, type = "registration" }) => {
  const colors = {
    confirmed: "bg-emerald-100 text-emerald-700",
    pending: "bg-amber-100 text-amber-700",
    pre_registered: "bg-violet-100 text-violet-700",
    tryout: "bg-blue-100 text-blue-700",
    waitlisted: "bg-slate-100 text-slate-700",
    declined: "bg-red-100 text-red-700",
    cancelled: "bg-red-100 text-red-700",
    paid: "bg-emerald-100 text-emerald-700",
    refunded: "bg-orange-100 text-orange-700",
    partial_refund: "bg-orange-100 text-orange-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || "bg-slate-100 text-slate-600"}`}>
      {(status || "unknown").replace(/_/g, " ")}
    </span>
  );
};

// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════
const RegistrationDetailPanel = ({
  open,
  onClose,
  registration,
  program,
  teams,
  token,
  onRefresh,
  onOpenRefund,
}) => {
  const [loading, setLoading] = useState(false);
  const [fullReg, setFullReg] = useState(null);
  const [player, setPlayer] = useState(null);
  const [household, setHousehold] = useState(null);
  const [waiverSignatures, setWaiverSignatures] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const downloadSignedWaiver = async (signatureId) => {
    try {
      const response = await axios.get(
        `${API}/waiver-signatures/download/${signatureId}`,
        { headers, responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      toast.error("Unable to download the signed waiver");
    }
  };

  // Fetch enriched data when panel opens
  useEffect(() => {
    if (open && registration) {
      fetchDetails();
    } else {
      setFullReg(null);
      setPlayer(null);
      setHousehold(null);
      setWaiverSignatures([]);
    }
  }, [open, registration?.registration_id]);

  const fetchDetails = async () => {
    if (!registration) return;
    setLoading(true);
    try {
      // Fetch full registration, player, and household in parallel
      const promises = [
        axios.get(`${API}/registrations/${registration.registration_id}`, { headers }).catch(() => null),
        axios.get(`${API}/waiver-signatures/registration/${registration.registration_id}`, { headers }).catch(() => null),
      ];
      if (registration.player_id) {
        promises.push(
          axios.get(`${API}/players/${registration.player_id}`, { headers }).catch(() => null)
        );
      }
      if (registration.household_id) {
        promises.push(
          axios.get(`${API}/households/${registration.household_id}`, { headers }).catch(() => null)
        );
      }

      const [regRes, sigsRes, playerRes, householdRes] = await Promise.all(promises);
      setFullReg(regRes?.data || registration);
      setWaiverSignatures(Array.isArray(sigsRes?.data) ? sigsRes.data : []);
      setPlayer(playerRes?.data || registration.player || null);
      setHousehold(householdRes?.data || registration.household || null);
    } catch (err) {
      console.error("Error fetching registration details:", err);
      // Fall back to data we already have
      setFullReg(registration);
      setPlayer(registration.player || null);
      setHousehold(registration.household || null);
    } finally {
      setLoading(false);
    }
  };

  // Merge what we have from list + full fetch
  const reg = fullReg || registration;
  const p = player || registration?.player || {};
  const h = household || registration?.household || {};
  const primaryContact = h?.primary_contact || {};
  const emergencyContacts = h?.emergency_contacts || p?.emergency_contacts || [];
  const team = teams?.find(t => t.team_id === reg?.team_id);
  const age = calculateAge(p?.date_of_birth);

  // Documents from program + registration
  const requiredDocs = program?.required_documents || [];
  const acceptedDocs = reg?.documents_accepted || [];

  // Payment schedule
  const paymentSchedule = reg?.payment_schedule || [];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto !bg-white p-0">
        {loading && !reg ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : reg ? (
          <div className="flex flex-col h-full">
            {/* ── Header ──────────────────────────────── */}
            <div className="p-6 border-b border-slate-200 bg-slate-50/50">
              <SheetHeader>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-sm">
                    {p?.photo_url ? (
                      <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xl font-semibold text-slate-500">
                        {(p?.first_name?.[0] || "")}{(p?.last_name?.[0] || "")}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="text-xl">
                      {p?.first_name || "Unknown"} {p?.last_name || "Player"}
                    </SheetTitle>
                    <SheetDescription className="flex items-center gap-2 mt-1 flex-wrap">
                      <StatusBadge status={reg.status} />
                      <StatusBadge status={reg.payment_status} type="payment" />
                      {team && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: team.color ? `${team.color}20` : "#f3f4f6", color: team.color || "#374151" }}>
                          {team.name}
                        </span>
                      )}
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
            </div>

            {/* ── Content ─────────────────────────────── */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">

              {/* Player Info */}
              <Section title="Player Information" icon={User} color="slate">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First Name" value={p?.first_name} />
                  <Field label="Last Name" value={p?.last_name} />
                  <Field label="Age" value={age != null ? `${age} years` : null} />
                  <Field label="Date of Birth" value={fmtDate(p?.date_of_birth)} />
                  <Field label="Gender" value={p?.gender ? p.gender.charAt(0).toUpperCase() + p.gender.slice(1) : null} />
                  {p?.jersey_number && <Field label="Jersey #" value={p.jersey_number} />}
                  {(reg?.jersey_size || p?.jersey_size) && <Field label="Jersey Size" value={reg?.jersey_size || p?.jersey_size} />}
                  {(reg?.short_size || p?.short_size) && <Field label="Short Size" value={reg?.short_size || p?.short_size} />}
                </div>
                {p?.address && (
                  <div className="mt-2">
                    <Field label="Address" value={
                      [p.address.street || p.address.line1, p.address.city, p.address.state, p.address.zip || p.address.postal_code]
                        .filter(Boolean).join(", ")
                    } />
                  </div>
                )}
              </Section>

              {/* Parent / Guardian Info */}
              <Section title="Parent / Guardian" icon={Users} color="blue">
                {primaryContact?.first_name || primaryContact?.name || reg?.payer?.name ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Name" value={
                        primaryContact?.first_name
                          ? `${primaryContact.first_name} ${primaryContact.last_name || ""}`.trim()
                          : primaryContact?.name || reg?.payer?.name
                      } />
                      {primaryContact?.relationship && (
                        <Field label="Relationship" value={primaryContact.relationship} />
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Field
                        label="Email"
                        value={primaryContact?.email || reg?.payer?.email || reg?.household_email}
                        href={primaryContact?.email ? `mailto:${primaryContact.email}` : null}
                      />
                      <Field
                        label="Phone"
                        value={primaryContact?.phone || reg?.household_phone}
                        href={primaryContact?.phone ? `tel:${primaryContact.phone}` : null}
                      />
                    </div>
                    {primaryContact?.address && (
                      <Field label="Address" value={
                        [primaryContact.address.street || primaryContact.address.line1, primaryContact.address.city, primaryContact.address.state, primaryContact.address.zip || primaryContact.address.postal_code]
                          .filter(Boolean).join(", ")
                      } />
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No parent/guardian information on file</p>
                )}

                {/* Secondary contacts from household */}
                {h?.secondary_contacts && h.secondary_contacts.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200 space-y-3">
                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wide">Additional Contacts</p>
                    {h.secondary_contacts.map((c, i) => (
                      <div key={i} className="grid grid-cols-2 gap-3">
                        <Field label="Name" value={c.first_name ? `${c.first_name} ${c.last_name || ""}`.trim() : c.name} />
                        <Field label="Phone" value={c.phone} href={c.phone ? `tel:${c.phone}` : null} />
                        {c.relationship && <Field label="Relationship" value={c.relationship} />}
                        {c.email && <Field label="Email" value={c.email} href={`mailto:${c.email}`} />}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Emergency Contacts */}
              <Section title="Emergency Contacts" icon={AlertTriangle} color="rose" defaultOpen={true}>
                {emergencyContacts.length > 0 ? (
                  emergencyContacts.map((ec, i) => (
                    <div key={i} className="grid grid-cols-2 gap-3">
                      <Field label="Name" value={ec.name || ec.first_name} />
                      <Field label="Phone" value={ec.phone} href={ec.phone ? `tel:${ec.phone}` : null} />
                      {ec.relationship && <Field label="Relationship" value={ec.relationship} />}
                    </div>
                  ))
                ) : (p?.emergency_contact_name || p?.emergency_contact_phone) ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Name" value={p.emergency_contact_name} />
                    <Field label="Phone" value={p.emergency_contact_phone} href={p.emergency_contact_phone ? `tel:${p.emergency_contact_phone}` : null} />
                    {p.emergency_contact_relationship && (
                      <Field label="Relationship" value={p.emergency_contact_relationship} />
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No emergency contacts on file</p>
                )}
              </Section>

              {/* Medical Info */}
              {(p?.medical_conditions || p?.medical_notes || p?.allergies || p?.medications) && (
                <Section title="Medical Information" icon={Heart} color="amber">
                  <div className="space-y-2">
                    {(p.medical_conditions || p.medical_notes) && (
                      <Field label="Medical Conditions / Notes" value={p.medical_conditions || p.medical_notes} />
                    )}
                    {p.allergies && <Field label="Allergies" value={p.allergies} />}
                    {p.medications && <Field label="Medications" value={p.medications} />}
                    {p.insurance_provider && (
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Insurance Provider" value={p.insurance_provider} />
                        <Field label="Policy Number" value={p.insurance_policy_number} />
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Payment Details */}
              <Section title="Payment Details" icon={CreditCard} color="emerald">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Total" value={fmtMoney(reg?.amount)} />
                  <Field label="Paid" value={fmtMoney(
                    reg?.amount_paid != null ? reg.amount_paid
                    : reg?.payment_status === 'paid' ? reg?.amount : 0
                  )} />
                  <Field label="Remaining" value={fmtMoney(
                    reg?.amount_remaining != null ? reg.amount_remaining
                    : reg?.payment_status === 'paid' ? 0 : reg?.amount
                  )} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Payment Plan" value={reg?.payment_plan || "Full Payment"} />
                  <Field label="Payment Status" value={
                    <StatusBadge status={reg?.payment_status} />
                  } />
                </div>
                {reg?.donation_amount > 0 && (
                  <Field label="Donation" value={fmtMoney(reg.donation_amount)} />
                )}
                {reg?.processing_fee > 0 && (
                  <Field label="Processing Fee" value={fmtMoney(reg.processing_fee)} />
                )}
                {reg?.stripe_payment_intent_id && (
                  <Field label="Stripe Payment ID" value={reg.stripe_payment_intent_id} />
                )}
                {reg?.promo_code && (
                  <Field label="Promo Code" value={reg.promo_code} />
                )}

                {/* Payment Schedule */}
                {paymentSchedule.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2">Payment Schedule</p>
                    <div className="space-y-2">
                      {paymentSchedule.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border border-emerald-100">
                          <div>
                            <span className="font-medium">{p.label || `Payment ${i + 1}`}</span>
                            {p.due_date && <span className="text-slate-500 ml-2">Due: {fmtDate(p.due_date)}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{fmtMoney(p.amount)}</span>
                            <StatusBadge status={p.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Refund button */}
                {reg?.payment_status === "paid" && onOpenRefund && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-amber-600 border-amber-200 hover:bg-amber-50"
                    onClick={() => onOpenRefund(reg)}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Process Refund
                  </Button>
                )}

                {/* Refund info if already refunded */}
                {(reg?.payment_status === "refunded" || reg?.payment_status === "partial_refund") && (
                  <div className="mt-2 p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm font-medium text-orange-700">
                      {reg.payment_status === "refunded" ? "Fully Refunded" : "Partially Refunded"}
                    </p>
                    {reg.refund_amount && (
                      <p className="text-sm text-orange-600">Refund amount: {fmtMoney(reg.refund_amount)}</p>
                    )}
                    {reg.refunded_at && (
                      <p className="text-xs text-orange-500 mt-1">Refunded on {fmtDateTime(reg.refunded_at)}</p>
                    )}
                  </div>
                )}
              </Section>

              {/* Signed Documents */}
              <Section
                title="Documents"
                icon={FileText}
                color="purple"
                badge={
                  requiredDocs.length > 0 && (
                    <span className="text-xs text-purple-600 font-medium ml-1">
                      {acceptedDocs.length}/{requiredDocs.length} signed
                    </span>
                  )
                }
              >
                {requiredDocs.length > 0 ? (
                  <div className="space-y-2">
                    {requiredDocs.map((doc, i) => {
                      const isSigned = acceptedDocs.includes(doc.doc_id || doc.name);
                      const sig = waiverSignatures.find(s => s.document_name === doc.name);
                      return (
                        <div key={i} className="bg-white rounded-lg px-3 py-2 border border-purple-100">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <FileText className={`w-4 h-4 ${sig ? "text-emerald-500" : isSigned ? "text-emerald-500" : "text-slate-400"}`} />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                                <p className="text-xs text-slate-500">{doc.type} • {doc.required ? "Required" : "Optional"}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sig ? (
                                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Signed</span>
                              ) : isSigned ? (
                                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Accepted</span>
                              ) : (
                                <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded-full">Not signed</span>
                              )}
                              {doc.content && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={() => {
                                    const blob = new Blob([doc.content], { type: "text/plain" });
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, "_blank");
                                    setTimeout(() => URL.revokeObjectURL(url), 5000);
                                  }}
                                  title="View document"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {sig && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-purple-600"
                                  onClick={() => downloadSignedWaiver(sig.signature_id)}
                                  title="Download signed PDF"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                          {sig && (
                            <div className="mt-2 pt-2 border-t border-purple-50 text-xs text-slate-500">
                              Signed by <span className="font-medium text-slate-700">{sig.signer_name}</span>
                              {sig.signer_relationship && ` (${sig.signer_relationship})`}
                              {sig.signed_at && ` on ${fmtDateTime(sig.signed_at)}`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No documents required for this program</p>
                )}
              </Section>

              {/* Addons / Merchandise */}
              {reg?.addons && reg.addons.length > 0 && (
                <Section title="Add-ons & Merchandise" icon={Package} color="slate">
                  <div className="space-y-2">
                    {reg.addons.map((addon, i) => (
                      <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100">
                        <div>
                          <p className="text-sm font-medium text-slate-900">{addon.name}</p>
                          {addon.size && <p className="text-xs text-slate-500">Size: {addon.size}</p>}
                          {addon.variant && <p className="text-xs text-slate-500">Variant: {addon.variant}</p>}
                        </div>
                        <span className="text-sm font-medium text-slate-900">{fmtMoney(addon.price)}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Friend Requests */}
              {reg?.friend_requests && reg.friend_requests.length > 0 && (
                <Section title="Friend Requests" icon={Heart} color="slate" defaultOpen={false}>
                  <div className="space-y-1">
                    {reg.friend_requests.map((fr, i) => (
                      <p key={i} className="text-sm text-slate-700">{fr.friend_name || fr.name || `Friend ${i + 1}`}</p>
                    ))}
                  </div>
                </Section>
              )}

              {/* Registration Metadata */}
              <Section title="Registration Details" icon={Clock} color="slate" defaultOpen={false}>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Registration ID" value={reg?.registration_id} />
                  <Field label="Registered" value={fmtDateTime(reg?.created_at)} />
                  {reg?.updated_at && <Field label="Last Updated" value={fmtDateTime(reg.updated_at)} />}
                  {reg?.division_id && <Field label="Division" value={reg.division?.name || reg.division_id} />}
                  {reg?.check_status && (
                    <Field label="Check-in Status" value={
                      <StatusBadge status={reg.check_status} />
                    } />
                  )}
                  {reg?.checked_in_at && <Field label="Checked In At" value={fmtDateTime(reg.checked_in_at)} />}
                </div>
              </Section>

            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export default RegistrationDetailPanel;
